/**
 * TeacherAiAssistantDrawer
 * ========================
 * Floating AI assistant panel for the Teacher Course View.
 * System palette: Navy Blue (#18216D), Gold (#FFB800 / #F59E0B), and Crisp White.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useApi } from '../../hooks/useApi';
import ConfirmModal from '../common/ConfirmModal';
import {
    ChatBubbleLeftRightIcon,
    XMarkIcon,
    PaperAirplaneIcon,
    ChevronDownIcon,
    TrashIcon,
    ArrowPathIcon,
    ClockIcon,
    ArrowsPointingOutIcon,
    ArrowsPointingInIcon,
    ArrowLeftIcon,
    PlusIcon,
    MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { ChatBubbleLeftRightIcon as ChatIconSolid } from '@heroicons/react/24/solid';

// ---------------------------------------------------------------------------
// Markdown renderer (bold, italic, bullet, links, code blocks, & HTML tables)
// ---------------------------------------------------------------------------
function renderMarkdown(text) {
    if (!text) return null;
    const lines = text.split('\n');
    const elements = [];
    let keyCounter = 0;

    let i = 0;
    while (i < lines.length) {
        const line = lines[i];
        const key = keyCounter++;

        // Fenced code block: ```
        if (line.trim().startsWith('```')) {
            const codeLines = [];
            i++;
            while (i < lines.length && !lines[i].trim().startsWith('```')) {
                codeLines.push(lines[i]);
                i++;
            }
            i++; // skip closing ```
            elements.push(
                <pre key={key} className="my-2 p-3 bg-slate-900 text-slate-100 rounded-xl text-xs font-mono overflow-x-auto shadow-inner border border-slate-800">
                    <code>{codeLines.join('\n')}</code>
                </pre>
            );
            continue;
        }

        // Markdown Table: | Header | Header |
        if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
            const tableRows = [];
            while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
                const rowText = lines[i].trim();
                // Ignore delimiter rows like |---|---|
                if (!/^\|[\s\-:|]+\|$/.test(rowText)) {
                    const cells = rowText
                        .slice(1, -1)
                        .split('|')
                        .map(c => c.trim());
                    tableRows.push(cells);
                }
                i++;
            }

            if (tableRows.length > 0) {
                const headerCells = tableRows[0];
                const bodyRows = tableRows.slice(1);

                elements.push(
                    <div key={key} className="my-3 overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white">
                        <table className="w-full text-xs text-left border-collapse">
                            <thead>
                                <tr className="bg-[#18216D] text-white font-black uppercase tracking-wider">
                                    {headerCells.map((cell, ci) => (
                                        <th key={ci} className="px-3.5 py-2.5 border-b border-indigo-900">
                                            {parseInline(cell)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {bodyRows.map((row, ri) => (
                                    <tr key={ri} className="hover:bg-slate-50/80 transition-colors even:bg-slate-50/40">
                                        {row.map((cell, ci) => (
                                            <td key={ci} className="px-3.5 py-2 text-slate-800 font-medium">
                                                {parseInline(cell)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
            }
            continue;
        }

        // Horizontal rule
        if (/^---+$/.test(line.trim())) {
            elements.push(<hr key={key} className="my-3 border-slate-200" />);
            i++;
            continue;
        }

        // Heading ### H3
        if (line.startsWith('### ')) {
            elements.push(
                <h3 key={key} className="font-black text-[#18216D] text-xs uppercase tracking-widest mt-3 mb-1">
                    {parseInline(line.slice(4))}
                </h3>
            );
            i++;
            continue;
        }
        // Heading ## H2
        if (line.startsWith('## ')) {
            elements.push(
                <h2 key={key} className="font-black text-[#18216D] text-sm mt-3 mb-1">
                    {parseInline(line.slice(3))}
                </h2>
            );
            i++;
            continue;
        }

        // Bullet list (FIXED ESLint regex)
        if (/^[-*] /.test(line)) {
            elements.push(
                <div key={key} className="flex items-start gap-2 ml-1 mb-1">
                    <span className="text-amber-500 font-bold mt-0.5 flex-shrink-0">•</span>
                    <span className="text-slate-800 text-sm leading-relaxed">{parseInline(line.slice(2))}</span>
                </div>
            );
            i++;
            continue;
        }

        // Numbered list
        if (/^\d+\. /.test(line)) {
            const match = line.match(/^(\d+)\. (.*)/);
            if (match) {
                elements.push(
                    <div key={key} className="flex items-start gap-2 ml-1 mb-1">
                        <span className="text-amber-600 font-black text-xs flex-shrink-0 mt-0.5">{match[1]}.</span>
                        <span className="text-slate-800 text-sm leading-relaxed">{parseInline(match[2])}</span>
                    </div>
                );
                i++;
                continue;
            }
        }

        // Empty line — spacing
        if (line.trim() === '') {
            elements.push(<div key={key} className="h-2" />);
            i++;
            continue;
        }

        // Regular paragraph
        elements.push(
            <p key={key} className="text-slate-800 text-sm leading-relaxed mb-1">
                {parseInline(line)}
            </p>
        );
        i++;
    }

    return elements;
}

function parseInline(text) {
    const parts = [];
    const regex = /(\[([^\]]+)\]\(([^)]+)\))|(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(`([^`]+)`)/g;
    let lastIndex = 0;
    let match;
    let key = 0;

    while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(<span key={key++}>{text.slice(lastIndex, match.index)}</span>);
        }

        if (match[1]) {
            // Link [text](url)
            const href = match[3];
            const isInternal = href.startsWith('/');
            parts.push(
                <a
                    key={key++}
                    href={href}
                    onClick={(e) => {
                        if (isInternal) {
                            e.preventDefault();
                            window.location.href = href;
                        }
                    }}
                    target={isInternal ? '_self' : '_blank'}
                    rel="noopener noreferrer"
                    className="underline text-[#18216D] hover:text-amber-600 font-bold transition-colors"
                >
                    {match[2]}
                </a>
            );
        } else if (match[4]) {
            // Bold **text**
            parts.push(<strong key={key++} className="font-black text-[#18216D]">{match[5]}</strong>);
        } else if (match[6]) {
            // Italic *text*
            parts.push(<em key={key++} className="italic text-slate-700">{match[7]}</em>);
        } else if (match[8]) {
            // Code `text`
            parts.push(
                <code key={key++} className="bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded text-[#18216D] text-xs font-mono">
                    {match[9]}
                </code>
            );
        }

        lastIndex = regex.lastIndex;
    }

    if (lastIndex < text.length) {
        parts.push(<span key={key++}>{text.slice(lastIndex)}</span>);
    }

    return parts.length > 0 ? parts : text;
}


// ---------------------------------------------------------------------------
// Tab-aware prompt questions (Full questions)
// ---------------------------------------------------------------------------
const PROMPT_BUBBLES = {
    gradebook: [
        { prompt: 'Which students are struggling most this term?' },
        { prompt: 'Show me outcome-level gaps across my class' },
    ],
    sow: [
        { prompt: 'Am I on track with my Scheme of Work this term?' },
        { prompt: 'What lessons does my Scheme of Work have for the upcoming weeks?' },
    ],
    records: [
        { prompt: 'Which lesson plans are missing reflections?' },
        { prompt: 'What content have I covered so far this term?' },
    ],
    quizzes: [
        { prompt: 'I want to draft quiz questions. What do I need to do first?' },
    ],
    assignments: [
        { prompt: 'How have my class performed across recent assessments?' },
    ],
    class_teacher_dashboard: [
        { prompt: 'Who in my stream has low attendance or is at academic risk?' },
        { prompt: 'Summarize the performance band distribution (EE, ME, AE, BE) for my stream' },
        { prompt: 'Which students need immediate remediation attention and why?' },
        { prompt: 'Draft a supportive academic check-in message for parents of struggling students' },
        { prompt: 'Show me the top academic performers and attendance champions in my stream' },
        { prompt: 'How is my class trending compared to last term?' },
        { prompt: 'Any discipline concerns in my class this term?' },
    ],
    hod_dashboard: [
        { prompt: 'Which teachers have not keyed in their Term 1 marks yet?' },
        { prompt: 'Compare stream performance across all the grades I manage' },
        { prompt: 'Which learning areas have lowest assessment completion?' },
        { prompt: 'How does this term compare to last term across my department?' },
        { prompt: 'Summarize curriculum coverage and SOW progress across all grades in my department' },
        { prompt: 'Are there any discipline trends across my department this term?' },
    ],
    principal_dashboard: [
        { prompt: 'How does overall academic performance compare between our departments and grade cohorts this term?' },
        { prompt: 'Which student cohorts or grades show the highest concentration of academic and attendance risk?' },
        { prompt: 'Summarize whole-school student discipline trends, merits, and behavioral patterns for this term' },
        { prompt: 'What curriculum strands or learning domains demonstrate strong school-wide competency versus those needing institutional focus?' },
        { prompt: 'Provide an executive summary of school-wide attendance rates and chronic absenteeism patterns' },
        { prompt: 'How does this term compare to last term school-wide?' },
        { prompt: 'Highlight significant performance anomalies across grade levels to prepare for upcoming leadership and board reviews' },
    ],
    default: [
        { prompt: 'Summarise my class\'s progress this term' },
        { prompt: 'What should I focus on teaching next based on my Scheme of Work?' },
    ],
};


// ---------------------------------------------------------------------------
// Message Bubble Component
// ---------------------------------------------------------------------------
function MessageBubble({ msg }) {
    const isUser = msg.role === 'user';
    return (
        <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
            {!isUser && (
                <div className="w-8 h-8 rounded-xl bg-[#18216D] flex items-center justify-center mr-2 flex-shrink-0 mt-0.5 shadow-sm">
                    <ChatIconSolid className="w-4 h-4 text-amber-400" />
                </div>
            )}
            <div
                className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed shadow-sm ${
                    isUser
                        ? 'bg-[#18216D] text-white rounded-tr-sm font-medium'
                        : 'bg-white text-slate-800 border border-slate-200/80 rounded-tl-sm'
                }`}
            >
                {isUser ? (
                    <span>{msg.content}</span>
                ) : (
                    <div className="space-y-0.5">
                        {renderMarkdown(msg.content)}
                    </div>
                )}
            </div>
        </div>
    );
}


// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
const TeacherAiAssistantDrawer = ({
    learningAreaId,
    activeTab,
    courseName,
    gradeName,
    dashboardType = '',
    schoolLevelId = null,
    gradeId = null,
    streamId = null,
    termId = null
}) => {
    const { get, post, del } = useApi();
    const [isOpen, setIsOpen] = useState(false);
    const [viewMode, setViewMode] = useState('chat'); // 'chat' | 'history'
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [currentSessionId, setCurrentSessionId] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [loadingSessions, setLoadingSessions] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null); // null | 'all' | session object
    const [isDeleting, setIsDeleting] = useState(false);
    const [threadSearchTerm, setThreadSearchTerm] = useState('');
    const [showScrollBtn, setShowScrollBtn] = useState(false);
    const [isTextareaOverflowed, setIsTextareaOverflowed] = useState(false);
    const [promptOffset, setPromptOffset] = useState(0);
    const messagesEndRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const inputRef = useRef(null);
    // True after the user starts a fresh thread, so history auto-loading does not refill it with old messages
    const newThreadRef = useRef(false);

    // Filter threads based on search term
    const filteredSessions = useMemo(() => {
        if (!threadSearchTerm.trim()) return sessions;
        const term = threadSearchTerm.toLowerCase();
        return sessions.filter(s =>
            (s.title || '').toLowerCase().includes(term) ||
            (s.latest_preview || '').toLowerCase().includes(term)
        );
    }, [sessions, threadSearchTerm]);

    // Resolve active prompt bank based on dashboardType or activeTab
    const currentTabKey = (dashboardType === 'principal')
        ? 'principal_dashboard'
        : (dashboardType === 'class_teacher' ? 'class_teacher_dashboard' : activeTab);
    const bubbles = PROMPT_BUBBLES[currentTabKey] || PROMPT_BUBBLES[activeTab] || PROMPT_BUBBLES.default;

    // Strict 2-bubble display window
    const displayedBubbles = bubbles.slice(promptOffset, promptOffset + 2);
    const totalPromptPages = Math.ceil(bubbles.length / 2);
    const currentPromptPage = Math.floor(promptOffset / 2) + 1;

    const handleNextPrompts = () => {
        setPromptOffset(prev => {
            const next = prev + 2;
            return next >= bubbles.length ? 0 : next;
        });
    };

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    // Load conversation history from database for a specific session or active context
    const loadHistory = useCallback(async (targetSessionId = null) => {
        if (newThreadRef.current && targetSessionId === null) return;
        try {
            const params = new URLSearchParams();
            if (learningAreaId) params.append('learning_area_id', learningAreaId);
            if (dashboardType) params.append('dashboard_type', dashboardType);
            const sid = targetSessionId !== null ? targetSessionId : currentSessionId;
            if (sid) params.append('session_id', sid);

            const history = await get(`/teachers/api/ai/history/?${params.toString()}`);
            if (Array.isArray(history) && history.length > 0) {
                setMessages(history.map(m => ({
                    role: m.role,
                    content: m.content,
                    created_at: m.created_at || null,
                    session_id: m.session_id || null
                })));
                if (!sid && history[history.length - 1]?.session_id) {
                    setCurrentSessionId(history[history.length - 1].session_id);
                }
            } else {
                setMessages([]);
            }
        } catch (err) {
            console.error('Failed to load AI history:', err);
        }
    }, [learningAreaId, dashboardType, currentSessionId, get]);

    // Fetch conversation thread sessions
    const loadSessions = useCallback(async () => {
        try {
            setLoadingSessions(true);
            const params = new URLSearchParams();
            if (learningAreaId) params.append('learning_area_id', learningAreaId);
            if (dashboardType) params.append('dashboard_type', dashboardType);
            const res = await get(`/teachers/api/ai/history/sessions/?${params.toString()}`);
            setSessions(Array.isArray(res) ? res : []);
        } catch (err) {
            console.error('Failed to load AI chat sessions:', err);
            setSessions([]);
        } finally {
            setLoadingSessions(false);
        }
    }, [learningAreaId, dashboardType, get]);

    useEffect(() => {
        if (isOpen) {
            if (viewMode === 'history') {
                loadSessions();
            } else {
                loadHistory();
                scrollToBottom();
                setTimeout(() => inputRef.current?.focus(), 300);
            }
        }
    }, [isOpen, viewMode, loadHistory, loadSessions, scrollToBottom]);

    // A different learning area / dashboard has its own threads
    useEffect(() => {
        newThreadRef.current = false;
    }, [learningAreaId, dashboardType]);

    // Reset prompt rotation offset when context changes
    useEffect(() => {
        setPromptOffset(0);
    }, [activeTab, learningAreaId, dashboardType]);

    const handleStartNewConversation = () => {
        newThreadRef.current = true;
        setCurrentSessionId(null);
        setMessages([]);
        setViewMode('chat');
        setTimeout(() => inputRef.current?.focus(), 200);
    };

    const handleSelectSession = async (sessionId) => {
        newThreadRef.current = false;
        setCurrentSessionId(sessionId);
        await loadHistory(sessionId);
        setViewMode('chat');
        setTimeout(() => scrollToBottom(), 200);
    };

    const sendMessage = useCallback(async (text) => {
        if (!text.trim() || loading) return;

        const now = new Date().toISOString();
        const userMessage = { role: 'user', content: text.trim(), created_at: now, session_id: currentSessionId };
        const updatedMessages = [...messages, userMessage];
        setMessages(updatedMessages);
        setInputValue('');
        setIsTextareaOverflowed(false);
        setLoading(true);

        try {
            const apiMessages = updatedMessages.map(m => ({
                role: m.role,
                content: m.content
            }));

            const response = await post('/teachers/api/ai/chat/', {
                messages: apiMessages,
                learning_area_id: learningAreaId || null,
                active_tab: activeTab || '',
                dashboard_type: dashboardType || '',
                school_level_id: schoolLevelId || null,
                grade_id: gradeId || null,
                stream_id: streamId || null,
                term_id: termId || null,
                session_id: currentSessionId || null
            });

            if (response?.session_id) {
                newThreadRef.current = false;
                setCurrentSessionId(response.session_id);
            }

            setMessages(prev => [
                ...prev,
                {
                    role: 'assistant',
                    content: response.response || 'No response received.',
                    created_at: new Date().toISOString(),
                    session_id: response.session_id || currentSessionId
                }
            ]);
        } catch (err) {
            console.error('AI chat error:', err);
            setMessages(prev => [
                ...prev,
                {
                    role: 'assistant',
                    content: 'I\'m having trouble connecting right now. Please try again in a moment.',
                    created_at: new Date().toISOString()
                }
            ]);
        } finally {
            setLoading(false);
        }
    }, [messages, loading, post, learningAreaId, activeTab, dashboardType, schoolLevelId, gradeId, streamId, termId, currentSessionId]);

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        setIsDeleting(true);
        try {
            if (deleteTarget === 'all') {
                const params = new URLSearchParams();
                if (learningAreaId) params.append('learning_area_id', learningAreaId);
                if (dashboardType) params.append('dashboard_type', dashboardType);
                await del(`/teachers/api/ai/history/?${params.toString()}`);
                newThreadRef.current = true;
                setMessages([]);
                setSessions([]);
                setCurrentSessionId(null);
                setViewMode('chat');
            } else if (deleteTarget.session_id) {
                const sid = deleteTarget.session_id;
                await del(`/teachers/api/ai/history/?session_id=${sid}`);
                setSessions(prev => prev.filter(s => s.session_id !== sid));
                if (currentSessionId === sid) {
                    newThreadRef.current = true;
                    setMessages([]);
                    setCurrentSessionId(null);
                }
            }
        } catch (err) {
            console.error('Failed to delete AI history:', err);
        } finally {
            setIsDeleting(false);
            setDeleteTarget(null);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        sendMessage(inputValue);
    };

    const handleBubbleClick = (prompt) => {
        sendMessage(prompt);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage(inputValue);
        }
    };

    const handleTextareaInput = (e) => {
        const target = e.target;
        target.style.height = 'auto';
        const newHeight = Math.min(target.scrollHeight, 110);
        target.style.height = `${newHeight}px`;
        setIsTextareaOverflowed(target.scrollHeight > 110);
    };

    return (
        <>
            {/* ----------------------------------------------------------------
                Floating Action Button (Gold Theme - Hidden when drawer open)
            ---------------------------------------------------------------- */}
            {!isOpen && (
                <button
                    id="ai-assistant-fab"
                    onClick={() => setIsOpen(true)}
                    className="fixed bottom-6 right-6 z-[300] group flex items-center gap-2.5 px-5 py-3.5 rounded-2xl bg-amber-400 hover:bg-amber-500 text-[#18216D] font-black text-xs uppercase tracking-widest shadow-xl shadow-amber-500/25 hover:scale-105 border border-amber-300 transition-all duration-300 active:scale-95"
                    style={{ bottom: '1.5rem', right: '1.5rem' }}
                    aria-label="Open AI Assistant"
                >
                    <ChatBubbleLeftRightIcon className="w-5 h-5 text-[#18216D] group-hover:scale-110 transition-transform" />
                    <span>AI Assistant</span>
                </button>
            )}

            {/* ----------------------------------------------------------------
                Drawer Panel (White background, Navy Header, Gold accents)
            ---------------------------------------------------------------- */}
            <div
                className={`fixed z-[300] transition-all duration-300 ease-out ${
                    isFullScreen
                        ? 'inset-3 md:inset-6'
                        : 'bottom-6 right-6'
                } ${
                    isOpen
                        ? 'translate-y-0 opacity-100 scale-100'
                        : 'translate-y-8 opacity-0 scale-95 pointer-events-none'
                }`}
                style={!isFullScreen ? { bottom: '1.5rem', right: '1.5rem' } : undefined}
            >
                <div className={`bg-white shadow-2xl border border-slate-200 overflow-hidden flex flex-col ${
                    isFullScreen
                        ? 'w-full h-full rounded-3xl'
                        : 'w-[440px] max-w-[calc(100vw-2rem)] h-[680px] max-h-[calc(100vh-100px)] rounded-[2rem]'
                } animate-in zoom-in-95 duration-200`}>
                    
                    {/* Header: Navy background with Grade & Subject info */}
                    <div className="bg-[#18216D] text-white px-6 py-4 flex items-center justify-between border-b border-indigo-950 flex-shrink-0 shadow-md">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-amber-400 flex items-center justify-center text-[#18216D] shadow-sm flex-shrink-0">
                                <ChatIconSolid className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="font-black text-white text-base tracking-tight leading-none">
                                    AI Teaching Assistant
                                </h3>
                                {(courseName || gradeName) && (
                                    <p className="text-amber-300 text-[11px] font-bold mt-1 tracking-wide">
                                        {courseName}{gradeName ? ` · ${gradeName}` : ''}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-1.5">
                            {/* New Conversation button (hidden when active chat is already new) */}
                            {(messages.length > 0 || viewMode === 'history') && (
                                <button
                                    type="button"
                                    onClick={handleStartNewConversation}
                                    className="p-2 text-slate-300 hover:text-white hover:bg-white/10 rounded-xl transition-all"
                                    title="New Conversation Thread"
                                    aria-label="Start new conversation"
                                >
                                    <PlusIcon className="w-5 h-5" />
                                </button>
                            )}

                            {/* History view toggle button */}
                            <button
                                type="button"
                                onClick={() => setViewMode(prev => prev === 'history' ? 'chat' : 'history')}
                                className={`p-2 rounded-xl transition-all ${
                                    viewMode === 'history'
                                        ? 'bg-amber-400 text-[#18216D] font-black shadow-sm'
                                        : 'text-slate-300 hover:text-white hover:bg-white/10'
                                }`}
                                title={viewMode === 'history' ? 'Back to Chat' : 'Conversation Threads'}
                                aria-label="Toggle chat history"
                            >
                                <ClockIcon className="w-5 h-5" />
                            </button>

                            {/* Full-Screen toggle button */}
                            <button
                                type="button"
                                onClick={() => setIsFullScreen(prev => !prev)}
                                className="p-2 text-slate-300 hover:text-white hover:bg-white/10 rounded-xl transition-all"
                                title={isFullScreen ? 'Exit full screen' : 'Full screen view'}
                                aria-label="Toggle full screen"
                            >
                                {isFullScreen ? (
                                    <ArrowsPointingInIcon className="w-5 h-5" />
                                ) : (
                                    <ArrowsPointingOutIcon className="w-5 h-5" />
                                )}
                            </button>

                            {/* Close button in header */}
                            <button
                                type="button"
                                onClick={() => { newThreadRef.current = false; setIsOpen(false); }}
                                className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-xl transition-all ml-1"
                                aria-label="Close drawer"
                            >
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>
                    </div>

                    {/* --------------------------------------------------------
                        BODY VIEW: History Panel vs Active Chat
                    -------------------------------------------------------- */}
                    {viewMode === 'history' ? (
                        /* History View */
                        <div className="flex-1 flex flex-col overflow-hidden bg-slate-50/70">
                            {/* History Sub-header */}
                            <div className="px-5 py-3 bg-white border-b border-slate-200 flex items-center justify-between shadow-xs flex-shrink-0">
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setViewMode('chat')}
                                        className="p-1.5 rounded-lg hover:bg-slate-100 text-[#18216D] transition-colors"
                                        title="Back to conversation"
                                    >
                                        <ArrowLeftIcon className="w-4 h-4 stroke-[2.5]" />
                                    </button>
                                    <div>
                                        <h4 className="font-black text-[#18216D] text-xs uppercase tracking-wider">
                                            Conversation Threads
                                        </h4>
                                        <p className="text-[10px] text-slate-400 font-bold">
                                            {sessions.length} {sessions.length === 1 ? 'thread' : 'threads'} saved
                                        </p>
                                    </div>
                                </div>

                                <div>
                                    {sessions.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => setDeleteTarget('all')}
                                            disabled={isDeleting}
                                            className={isFullScreen
                                                ? "inline-flex items-center gap-1.5 text-xs font-bold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 px-3 py-1.5 rounded-xl transition-all shadow-xs active:scale-95 disabled:opacity-50"
                                                : "p-2 text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-xl transition-all shadow-xs active:scale-95 disabled:opacity-50"
                                            }
                                            title="Clear all conversation history"
                                        >
                                            <TrashIcon className="w-4 h-4" />
                                            {isFullScreen && <span>Clear All</span>}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* History Items List (Compact high-density view) */}
                            <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                                <div className={`${isFullScreen ? 'max-w-4xl mx-auto' : 'w-full'} space-y-2`}>
                                    {/* Search Filter for Threads */}
                                    {sessions.length > 2 && (
                                        <div className="relative mb-2">
                                            <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                            <input
                                                type="text"
                                                value={threadSearchTerm}
                                                onChange={(e) => setThreadSearchTerm(e.target.value)}
                                                placeholder="Filter conversations..."
                                                className="w-full pl-9 pr-8 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-[#18216D]"
                                            />
                                            {threadSearchTerm && (
                                                <button
                                                    type="button"
                                                    onClick={() => setThreadSearchTerm('')}
                                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                                >
                                                    <XMarkIcon className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    {loadingSessions ? (
                                        <div className="flex flex-col items-center justify-center py-16 text-center">
                                            <ArrowPathIcon className="w-8 h-8 text-[#18216D] animate-spin mb-3" />
                                            <p className="text-xs font-bold text-slate-500">Loading conversation threads...</p>
                                        </div>
                                    ) : filteredSessions.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-12 text-center bg-white rounded-2xl border border-slate-100 p-6">
                                            <div className="w-12 h-12 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center mb-2 shadow-xs">
                                                <ClockIcon className="w-6 h-6 text-amber-600" />
                                            </div>
                                            <p className="text-slate-700 text-xs font-bold mb-0.5">
                                                {threadSearchTerm ? 'No matching threads found' : 'No past conversations'}
                                            </p>
                                            <p className="text-slate-400 text-[11px] max-w-xs">
                                                {threadSearchTerm ? 'Try searching with a different keyword' : 'Start asking questions to build your conversation history.'}
                                            </p>
                                        </div>
                                    ) : (
                                        filteredSessions.map(s => {
                                            const isCurrent = currentSessionId === s.session_id;
                                            const formattedDate = new Date(s.updated_at).toLocaleDateString([], {
                                                month: 'short',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            });

                                            return (
                                                <div
                                                    key={s.session_id}
                                                    onClick={() => handleSelectSession(s.session_id)}
                                                    className={`p-3 rounded-2xl border transition-all cursor-pointer group flex items-center justify-between gap-3 ${
                                                        isCurrent
                                                            ? 'bg-amber-50/20 border-amber-300 ring-2 ring-amber-400/20 shadow-xs'
                                                            : 'bg-white border-slate-200/80 hover:border-[#18216D]/40 hover:shadow-xs'
                                                    }`}
                                                >
                                                    <div className="flex items-center gap-3 min-w-0 flex-1">
                                                        <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                                                            isCurrent ? 'bg-[#18216D] text-amber-400' : 'bg-slate-100 text-[#18216D] group-hover:bg-indigo-50'
                                                        }`}>
                                                            <ChatBubbleLeftRightIcon className="w-4 h-4" />
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <div className="flex items-center justify-between gap-2">
                                                                <div className="flex items-center gap-1.5 min-w-0">
                                                                    <p className="text-xs font-black text-[#18216D] truncate leading-tight group-hover:text-indigo-900 transition-colors">
                                                                        {s.title}
                                                                    </p>
                                                                    {isCurrent && (
                                                                        <span className="text-[8px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded shrink-0">
                                                                            Active
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <span className="text-[10px] text-slate-400 font-bold shrink-0">
                                                                    {formattedDate}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center justify-between gap-2 text-[10px] text-slate-400 font-medium mt-1">
                                                                <span className="truncate text-slate-500 min-w-0">
                                                                    {s.latest_preview ? s.latest_preview.replace(/[#*`]/g, '') : 'Conversation thread'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-1 shrink-0">
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setDeleteTarget(s);
                                                            }}
                                                            className="p-1.5 rounded-lg text-slate-300 hover:text-rose-600 hover:bg-rose-50 opacity-70 group-hover:opacity-100 transition-all"
                                                            title="Delete this conversation"
                                                        >
                                                            <TrashIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        /* Active Chat View */
                        <>
                            {/* Messages Body */}
                            <div
                                ref={scrollContainerRef}
                                onScroll={(e) => {
                                    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
                                    setShowScrollBtn(scrollHeight - scrollTop - clientHeight > 100);
                                }}
                                className="flex-1 overflow-y-auto p-5 space-y-2 custom-scrollbar bg-slate-50/60"
                            >
                                <div className={`${isFullScreen ? 'max-w-4xl mx-auto' : 'w-full'}`}>
                                    {/* Empty state */}
                                    {messages.length === 0 && (
                                        <div className="flex flex-col items-center justify-center py-6 text-center">
                                            <div className="w-14 h-14 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center mb-3 shadow-sm">
                                                <ChatBubbleLeftRightIcon className="w-7 h-7 text-amber-600" />
                                            </div>
                                            <p className="text-slate-500 text-sm font-bold mb-0.5">Ask me anything about</p>
                                            <p className="text-[#18216D] font-black text-base">your class, curriculum, or progress</p>
                                            <p className="text-slate-400 text-[10px] font-bold mt-2 uppercase tracking-widest">Reads live class data</p>
                                        </div>
                                    )}

                                    {/* Messages */}
                                    {messages.map((msg, idx) => (
                                        <MessageBubble key={idx} msg={msg} />
                                    ))}

                                    {/* Loading indicator */}
                                    {loading && (
                                        <div className="flex justify-start mb-3">
                                            <div className="w-8 h-8 rounded-xl bg-[#18216D] flex items-center justify-center mr-2 flex-shrink-0 mt-0.5">
                                                <ChatIconSolid className="w-4 h-4 text-amber-400 animate-pulse" />
                                            </div>
                                            <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-2 shadow-sm">
                                                <span className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                                <span className="w-2 h-2 bg-[#18216D] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                                <span className="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                            </div>
                                        </div>
                                    )}

                                    <div ref={messagesEndRef} />
                                </div>
                            </div>

                            {/* Scroll to bottom floating button */}
                            {showScrollBtn && (
                                <div className="absolute bottom-28 right-6 z-10">
                                    <button
                                        onClick={scrollToBottom}
                                        className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center text-[#18216D] hover:bg-slate-50 transition-all shadow-md"
                                    >
                                        <ChevronDownIcon className="w-4 h-4" />
                                    </button>
                                </div>
                            )}

                            {/* Stacked Full-Question Prompt Bubbles (strictly 2 at a time with cycle button) */}
                            {messages.length === 0 && (
                                <div className="px-5 py-3.5 bg-white border-t border-slate-100 flex flex-col gap-2 flex-shrink-0">
                                    <div className={`${isFullScreen ? 'max-w-4xl mx-auto w-full' : 'w-full'}`}>
                                        <div className="flex items-center justify-between mb-1.5 px-1">
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Suggested Questions</p>
                                            {bubbles.length > 2 && (
                                                <button
                                                    type="button"
                                                    onClick={handleNextPrompts}
                                                    className="inline-flex items-center gap-1.5 text-[10px] font-black text-amber-600 hover:text-amber-800 uppercase tracking-wider transition-colors cursor-pointer bg-amber-50 hover:bg-amber-100 px-2.5 py-1 rounded-lg border border-amber-200"
                                                    title="Rotate questions"
                                                >
                                                    <ArrowPathIcon className="w-3 h-3 text-amber-600" />
                                                    <span>More suggestions ({currentPromptPage}/{totalPromptPages})</span>
                                                </button>
                                            )}
                                        </div>
                                        <div className={`grid ${isFullScreen ? 'grid-cols-2 gap-3' : 'grid-cols-1 gap-2'}`}>
                                            {displayedBubbles.map((bubble, idx) => (
                                                <button
                                                    key={idx}
                                                    onClick={() => handleBubbleClick(bubble.prompt)}
                                                    disabled={loading}
                                                    className="w-full text-left p-3 bg-slate-50 hover:bg-[#18216D] text-slate-700 hover:text-white border border-slate-200/80 hover:border-[#18216D] rounded-xl text-xs font-bold transition-all shadow-sm flex items-center justify-between group disabled:opacity-50"
                                                >
                                                    <span>{bubble.prompt}</span>
                                                    <span className="text-amber-500 group-hover:text-amber-300 transition-colors ml-2 font-black">→</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Textarea Input Section */}
                            <div className="p-4 bg-white border-t border-slate-100 flex-shrink-0">
                                <div className={`${isFullScreen ? 'max-w-4xl mx-auto w-full' : 'w-full'}`}>
                                    <form onSubmit={handleSubmit} className="flex items-end gap-2.5">
                                        <div className="flex-1 relative">
                                            <textarea
                                                ref={inputRef}
                                                value={inputValue}
                                                onChange={e => setInputValue(e.target.value)}
                                                onKeyDown={handleKeyDown}
                                                placeholder="Ask about your class, curriculum, or progress..."
                                                rows={1}
                                                disabled={loading}
                                                onInput={handleTextareaInput}
                                                className={`w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 focus:border-[#18216D] focus:bg-white rounded-2xl text-slate-900 placeholder:text-slate-400 placeholder:font-medium text-sm outline-none transition-all resize-none min-h-[46px] max-h-[110px] disabled:opacity-50 ${
                                                    isTextareaOverflowed ? 'overflow-y-auto custom-scrollbar' : 'overflow-hidden'
                                                }`}
                                            />
                                        </div>
                                        <button
                                            type="submit"
                                            disabled={!inputValue.trim() || loading}
                                            className="w-11 h-11 flex-shrink-0 rounded-2xl bg-[#18216D] hover:bg-[#0D164F] text-amber-400 flex items-center justify-center shadow-md hover:scale-105 active:scale-95 transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:scale-100"
                                        >
                                            <PaperAirplaneIcon className="w-5 h-5" />
                                        </button>
                                    </form>
                                </div>
                            </div>
                        </>
                    )}

                </div>
            </div>

            {/* Custom Confirmation Modal for Deleting History */}
            <ConfirmModal
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleConfirmDelete}
                title={deleteTarget === 'all' ? "Clear All Conversation History" : "Delete Conversation Thread"}
                message={deleteTarget === 'all'
                    ? "Are you sure you want to delete all saved conversation threads? This action cannot be undone."
                    : `Are you sure you want to delete "${deleteTarget?.title || 'this conversation'}"? This action cannot be undone.`}
                confirmText={deleteTarget === 'all' ? "Clear All" : "Delete"}
                type="danger"
                loading={isDeleting}
                maxWidth="max-w-md"
            />
        </>
    );
};

export default TeacherAiAssistantDrawer;
